import "./App.scss";
import "./styles/global.scss";
import "./styles/shadcn.css";
import React, { useEffect } from "react";
import LocalAuthorityDashboard from "./screens/LocalAuthority/Dashboard";
import Placements from "./screens/LocalAuthority/Dashboard/Placements";
import { HashRouter, Routes, Route, useNavigate } from "react-router-dom";
import { fetcher, GOOGLE_API_KEY, serverUrl, swrConfig } from "./helpers/utility";
import useSWR from "swr";
import Login from "./screens/shared/login";
import Search from "./screens/shared/Search";
import Loading from "./components/loading";
// REQUIRED for Bootstrap icons, hard to find initiation line!
import "bootstrap-icons/font/bootstrap-icons.css";
import { useLoadScript } from "@react-google-maps/api";
import PageNotFound from "./screens/shared/PageNotFound";
import LocalAuthoritySignUp from "./screens/shared/localAuthoritySignUp";
import axios from "axios";
import AddLocation from "./screens/Provider/AddLocation";
import ProviderSignUp from "./screens/shared/providerSignUp";
import ProviderDashboard from "./screens/Provider/Dashboard";
import ProviderServices from "./screens/Provider/Dashboard/Services";
import AddPlacement from "./screens/Provider/AddPlacement";
import LocationDetails from "./screens/shared/LocationDetails";
import AdminDashboard from "./screens/Admin/AdminDashboard";
import AdminLocation from "./screens/Admin/AdminLocation";
import AdminOrganisation from "./screens/Admin/AdminOrganisation";
import AdminLocalAuthority from "./screens/Admin/AdminLocalAuthority";
import AdminService from "./screens/Admin/AdminService";
import Enquiry from "./screens/shared/Enquiry";
import NotLoggedIn from "./screens/shared/NotLoggedIn";
import HomePage from "./screens/shared/Home";
import Insights from './screens/LocalAuthority/Dashboard/Insights';
import Services from './screens/LocalAuthority/Dashboard/Services';
import Locations from './screens/Provider/Dashboard/Locations';
import TeamManagement from './screens/LocalAuthority/Dashboard/Team/TeamManagement';
import ProviderTeamManagement from './screens/Provider/Dashboard/Team/TeamManagement';
import AcceptInvite from './screens/shared/AcceptInvite';
import ForgotPassword from "./screens/shared/ForgotPassword";
import ResetPassword from "./screens/shared/ResetPassword";
import VerifyEmail from "./screens/shared/VerifyEmail";
axios.defaults.withCredentials = true;
axios.defaults.timeout = 4000;

const mapLibraries = ["places", "geocoding"];

function App() {
  // Load the Google Maps API
  const { isLoaded: isGoogleMapLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: mapLibraries,
    region: "GB",
  });

  const token = localStorage.getItem("token");

  const {
    data: userData,
    error: userError,
    isLoading: userIsLoading,
  } = useSWR(`${serverUrl}/user`, fetcher, swrConfig);

  const {
    data: locationData,
    error: locationError,
    isLoading: locationIsLoading,
  } = useSWR(`${serverUrl}/location`, fetcher, swrConfig);

  useEffect(() => {
    if (userData?.user) {
      if (
        // Redirect logged in user from top level page to actual path
        window.location.hash === "" ||
        window.location.hash === "#/" ||
        window.location.href?.includes("login") ||
        window.location.href?.includes("provider-sign-up") ||
        window.location.href?.includes("local-authority-sign-up")
      ) {
        if (userData.user.localAuthorityId) {
          window.location.href = "#/local-authority/dashboard";
        } else if (userData.user.careProviderOrganisationId) {
          window.location.href = "#/provider/dashboard";
        } else {
          window.location.href = "#/admin/dashboard";
        }
      }
    }
  }, [userData]);

  // Don't redirect if user is on accept-invite page
  if( (window.location.hash === "" || window.location.hash === "#/") && !window.location.hash.includes("accept-invite")) {
    window.location.href = "#/login";
  }

  // Allow accept-invite page to load even during loading states
  if (window.location.hash.includes("accept-invite")) {
    return (
      <HashRouter>
        <div className="App">
          <Routes>
            <Route path="/accept-invite" element={<AcceptInvite />} />
          </Routes>
        </div>
      </HashRouter>
    );
  }

  if (userIsLoading || locationIsLoading) {
    return <Loading />;
  }

  if (userIsLoading === false && (!userData?.user?._id || userError)) {
    console.log(`User is not authenticated`);

    return (
      <HashRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/local-authority-sign-up" element={<LocalAuthoritySignUp />} />
            <Route path="/provider-sign-up" element={<ProviderSignUp />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* <Route path="*" element={<NotLoggedIn />} /> */}
          </Routes>
        </div>
      </HashRouter>
    );
  }

  console.log(`User Data: ${JSON.stringify(userData)}`);
  const user = userData?.user;

  return (
    <HashRouter>
      <div className="App">
        <Routes>
          {/* Local Authority Dashboard */}
          <Route
            path="/"
            element={<LocalAuthorityDashboard user={user} isGoogleMapLoaded={isGoogleMapLoaded} />}
          />
          <Route
            path="/local-authority/dashboard"
            element={<LocalAuthorityDashboard user={user} isGoogleMapLoaded={isGoogleMapLoaded} />}
          />
          <Route
            path="/local-authority/dashboard/placements"
            element={<Placements user={user} />}
          />
          <Route path="/local-authority/dashboard/insights" element={<Insights user={user} />} />
          <Route path="/local-authority/dashboard/services" element={<Services user={user} />} />
          <Route path="/local-authority/dashboard/team" element={<TeamManagement user={user} />} />

          {/* Provider Dashboard */}
          <Route path="/provider/dashboard" element={<ProviderDashboard user={user} />} />
          <Route path="/provider/dashboard/locations" element={<Locations user={user} />} />
          <Route path="/provider/dashboard/services" element={<ProviderServices user={user} />} />  
          <Route path="/provider/dashboard/team" element={<ProviderTeamManagement user={user} />} />

          {/* Admin Dashboard */}
          <Route path="/admin/dashboard" element={<AdminDashboard user={user} />} />
          <Route path="/admin/location" element={<AdminLocation user={user} />} />
          <Route path="/admin/organisation" element={<AdminOrganisation user={user} />} />
          <Route path="/admin/local-authority" element={<AdminLocalAuthority user={user} />} />
          <Route path="/admin/service" element={<AdminService user={user} />} />

          {/* Shared */}
          <Route
            path="/organisation"
            element={<LocalAuthorityDashboard user={user} isGoogleMapLoaded={isGoogleMapLoaded} />}
          />
          <Route path="/enquiry" element={<Enquiry user={user} />} />
          <Route
            path="/search"
            element={
              <Search
                locationsRaw={locationData?.locations}
                user={user}
                isGoogleMapLoaded={isGoogleMapLoaded}
              />
            }
          />
          <Route path="/add-location" element={<AddLocation user={user} />} />
          <Route
            path="/location"
            element={<LocationDetails user={user} isGoogleMapLoaded={isGoogleMapLoaded} />}
          />
          <Route
            path="/add-placement"
            element={<AddPlacement user={user} isGoogleMapLoaded={isGoogleMapLoaded} />}
          />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="*" element={<PageNotFound user={user} />} />
        </Routes>
      </div>
    </HashRouter>
  );
}

export default App;
